import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  LineController,
} from 'chart.js';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  LineController
);

export default function SummaryBoxTestPlans({
  plans,
  title,
  loading,
  mode,
  rawPlans = [],
}) {
  const planList = useMemo(() => {
    return Object.entries(plans || {}).flatMap(([arch, tests]) =>
      Array.isArray(tests)
        ? tests.map((t) => ({
            ...t,
            arch,
          }))
        : []
    );
  }, [plans]);

  const total = planList.length;

  const archCounts = useMemo(() => {
    return planList.reduce((acc, { arch }) => {
      acc[arch] = (acc[arch] || 0) + 1;
      return acc;
    }, {});
  }, [planList]);

  const squadCounts = useMemo(() => {
    return planList.reduce((acc, { squad }) => {
      acc[squad] = (acc[squad] || 0) + 1;
      return acc;
    }, {});
  }, [planList]);

  const modulesPerBuildData = useMemo(() => {
    const buildMap = {};

    for (const plan of rawPlans) {
      let build = plan.build;
      if (!build || typeof build !== 'string') {
        build = 'unknown';
      } else {
        build = build.trim();
      }

      const moduleCount = Array.isArray(plan.modules) ? plan.modules.length : 0;
      buildMap[build] = (buildMap[build] || 0) + moduleCount;
    }

    const sortedBuilds = Object.entries(buildMap)
      .filter(([build]) => build !== 'unknown')
      .sort(([a], [b]) => {
        const numA = parseFloat(a.replace(/[^\d.]/g, '')) || 0;
        const numB = parseFloat(b.replace(/[^\d.]/g, '')) || 0;
        return numA - numB;
      })
      .slice(-10);

    if (buildMap.unknown) {
      sortedBuilds.push(['unknown', buildMap.unknown]);
    }

    const labels = sortedBuilds.map(([build]) => build);
    const values = sortedBuilds.map(([_, count]) => count);

    const trendLine = values.map((_, i, arr) => {
      const window = arr.slice(Math.max(0, i - 2), i + 1);
      const avg = window.reduce((a, b) => a + b, 0) / window.length;
      return Math.round(avg);
    });

    return {
      labels,
      datasets: [
        {
          type: 'line',
          label: 'Moving average',
          data: trendLine,
          borderColor: '#b97c4e',
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.4,
          fill: false,
        },
        {
          type: 'bar',
          label: 'Modules execution per Build',
          data: values,
          backgroundColor: '#c3e6cb',
          borderRadius: 4,
        },
      ],
    };
  }, [rawPlans]);

  const modulesBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      x: {
        title: { display: true, text: 'Builds' },
      },
      y: {
        title: { display: true, text: 'Modules' },
        beginAtZero: true,
      },
    },
  };

  const archChartData = {
    labels: Object.keys(archCounts),
    datasets: [
      {
        data: Object.values(archCounts),
        backgroundColor: [
          '#d4edda',
          '#c3e6cb',
          '#a8ddb5',
          '#8fc69e',
          '#73ba25',
          '#5ea917',
          '#4e9a0c',
          '#3e8c02',
          '#2e7c00',
          '#1e6d00',
        ],
        borderWidth: 1,
      },
    ],
  };

  return (
    <div className="summary-box">
      <div className="summary-title">{title}</div>
      {loading ? (
        <div className="summary-total">Loading summary...</div>
      ) : (
        <>
          <div className="summary-total">Total scenarios: {total}</div>

          {}
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              marginTop: '1rem',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: '1 1 300px', minWidth: 260 }}>
              <strong>Test Scenarios per architectures:</strong>
              <div className="tool-counts mb-2">
                {Object.entries(archCounts).map(([arch, count]) => (
                  <span key={arch} className="tool-item">
                    {arch}: {count}
                  </span>
                ))}
              </div>
              <div style={{ height: 220 }}>
                <Doughnut
                  data={archChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    cutout: '60%',
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          boxWidth: 10,
                          font: {
                            size: 10,
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>

            <div style={{ flex: '2 1 400px', minWidth: 300 }}>
              <strong>Modules execution per build:</strong>
              <div style={{ height: 250 }}>
                <Bar data={modulesPerBuildData} options={modulesBarOptions} />
              </div>
            </div>
          </div>

          {/* Squads */}
          {mode !== 'squad' && (
            <div className="summary-section mt-4">
              <strong>Test Scenarios per squads:</strong>
              <div className="tool-counts">
                {Object.entries(squadCounts).map(([squad, count]) => (
                  <span key={squad} className="tool-item">
                    {squad}: {count}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

SummaryBoxTestPlans.propTypes = {
  plans: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  mode: PropTypes.string,
  rawPlans: PropTypes.array,
};

SummaryBoxTestPlans.defaultProps = {
  loading: false,
  rawPlans: [],
};
